/**
 * Import Service - Handles company import API operations
 *
 * This service manages the import workflow for existing LLCs:
 * - Initiate import
 * - Upload documents
 * - Select RA option
 * - Submit for review
 * - Create payment for RA transfer
 */

import environmentConfig from '../config/environment';
import logger from '../utils/logger';
import { appendAcquisitionMetadata } from './acquisitionMetadata';

class ImportService {
  constructor() {
    this.apiBase = environmentConfig.api.baseURL;
    this.endpoints = {
      initiate: '/api/import/initiate',
      documentRequirements: '/api/import/document-requirements',
      uploadUrl: (importId) => `/api/import/${importId}/documents/upload-url`,
      confirmUpload: (importId) => `/api/import/${importId}/documents`,
      deleteDocument: (importId, documentId) =>
        `/api/import/${importId}/documents/${documentId}`,
      raOption: (importId) => `/api/import/${importId}/ra-option`,
      submit: (importId) => `/api/import/${importId}/submit`,
      payment: (importId) => `/api/import/${importId}/payment`,
      manualPayment: (importId) => `/api/import/${importId}/payment/manual`,
      confirmPayment: (importId) => `/api/import/${importId}/payment/confirm`,
      getImport: (importId) => `/api/import/${importId}`,
      myImports: '/api/import/my-imports',
      cancel: (importId) => `/api/import/${importId}/cancel`,
    };
  }

  getAuthHeaders(token) {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Initiate a new company import
   */
  async initiateImport(data, token) {
    const url = `${this.apiBase}${this.endpoints.initiate}`;
    const payload = appendAcquisitionMetadata(data);
    logger.debug('Initiating company import', {
      companyName: data.company_name,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to initiate import');
      }

      logger.info('Company import initiated successfully', {
        importId: result.data?.import_id,
      });
      return result;
    } catch (error) {
      logger.error('Error initiating import', error);
      throw error;
    }
  }

  /**
   * Get document requirements
   */
  async getDocumentRequirements(token) {
    const url = `${this.apiBase}${this.endpoints.documentRequirements}`;
    logger.debug('Fetching document requirements');

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message || 'Failed to get document requirements'
        );
      }

      return result;
    } catch (error) {
      logger.error('Error fetching document requirements', error);
      throw error;
    }
  }

  /**
   * Get presigned upload URL for a document
   */
  async getUploadUrl(importId, documentData, token) {
    const url = `${this.apiBase}${this.endpoints.uploadUrl(importId)}`;
    logger.debug('Getting upload URL', {
      importId,
      documentType: documentData.document_type,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(documentData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to get upload URL');
      }

      return result;
    } catch (error) {
      logger.error('Error getting upload URL', error);
      throw error;
    }
  }

  /**
   * Upload file directly to storage using presigned URL
   */
  async uploadFileToStorage(uploadUrl, file) {
    logger.debug('Uploading file to storage', {
      fileName: file.name,
      size: file.size,
    });

    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to upload file to storage');
      }

      logger.info('File uploaded to storage successfully', {
        fileName: file.name,
      });
      return true;
    } catch (error) {
      logger.error('Error uploading file to storage', error);
      throw error;
    }
  }

  /**
   * Confirm document upload after file is in storage
   */
  async confirmDocumentUpload(importId, storageKey, documentData, token) {
    const url = `${this.apiBase}${this.endpoints.confirmUpload(
      importId
    )}?storage_key=${encodeURIComponent(storageKey)}`;
    logger.debug('Confirming document upload', {
      importId,
      documentType: documentData.document_type,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(documentData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to confirm document upload');
      }

      logger.info('Document upload confirmed', {
        importId,
        documentType: documentData.document_type,
      });
      return result;
    } catch (error) {
      logger.error('Error confirming document upload', error);
      throw error;
    }
  }

  /**
   * Upload document directly through backend (avoids CORS issues)
   */
  async uploadDocument(importId, file, documentType, token) {
    logger.info('Starting document upload', {
      importId,
      documentType,
      fileName: file.name,
    });

    const url = `${this.apiBase}/api/import/${importId}/documents/upload`;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Note: Don't set Content-Type for FormData - browser sets it with boundary
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to upload document');
      }

      logger.info('Document uploaded successfully', {
        importId,
        documentType,
      });
      return result;
    } catch (error) {
      logger.error('Document upload failed', error);
      throw error;
    }
  }

  /**
   * Complete document upload workflow: get URL, upload, confirm (legacy - use uploadDocument instead)
   */
  async uploadDocumentViaPresignedUrl(importId, file, documentType, token) {
    logger.info('Starting document upload workflow (presigned URL)', {
      importId,
      documentType,
      fileName: file.name,
    });

    try {
      // Step 1: Get upload URL
      const documentData = {
        document_type: documentType,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
      };

      const uploadUrlResponse = await this.getUploadUrl(
        importId,
        documentData,
        token
      );
      const { upload_url, storage_key } = uploadUrlResponse.data;

      // Step 2: Upload file to storage
      await this.uploadFileToStorage(upload_url, file);

      // Step 3: Confirm upload
      const confirmResponse = await this.confirmDocumentUpload(
        importId,
        storage_key,
        documentData,
        token
      );

      return confirmResponse;
    } catch (error) {
      logger.error('Document upload workflow failed', error);
      throw error;
    }
  }

  /**
   * Delete a document from import
   */
  async deleteDocument(importId, documentId, token) {
    const url = `${this.apiBase}${this.endpoints.deleteDocument(
      importId,
      documentId
    )}`;
    logger.debug('Deleting document', { importId, documentId });

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete document');
      }

      logger.info('Document deleted', { importId, documentId });
      return result;
    } catch (error) {
      logger.error('Error deleting document', error);
      throw error;
    }
  }

  /**
   * Set members for an import
   */
  async setMembers(importId, members, token) {
    const url = `${this.apiBase}/api/import/${importId}/members`;
    logger.debug('Setting members for import', {
      importId,
      memberCount: members.length,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify({ members }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to set members');
      }

      logger.info('Members set successfully', { importId });
      return result;
    } catch (error) {
      logger.error('Error setting members', error);
      throw error;
    }
  }

  /**
   * Select RA option for import
   */
  async selectRaOption(importId, raOption, currentRaInfo, token) {
    const url = `${this.apiBase}${this.endpoints.raOption(importId)}`;
    logger.debug('Selecting RA option', { importId, raOption });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify({
          ra_option: raOption,
          current_ra_name: currentRaInfo?.name,
          current_ra_address: currentRaInfo?.address,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to select RA option');
      }

      logger.info('RA option selected', { importId, raOption });
      return result;
    } catch (error) {
      logger.error('Error selecting RA option', error);
      throw error;
    }
  }

  /**
   * Submit import for review
   */
  async submitForReview(importId, token) {
    const url = `${this.apiBase}${this.endpoints.submit(importId)}`;
    logger.debug('Submitting import for review', { importId });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit for review');
      }

      logger.info('Import submitted for review', { importId });
      return result;
    } catch (error) {
      logger.error('Error submitting for review', error);
      throw error;
    }
  }

  /**
   * Create payment for RA transfer
   */
  async createPayment(importId, token) {
    const url = `${this.apiBase}${this.endpoints.payment(importId)}`;
    logger.debug('Creating payment for RA transfer', { importId });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create payment');
      }

      logger.info('Payment created', { importId });
      return result;
    } catch (error) {
      logger.error('Error creating payment', error);
      throw error;
    }
  }

  /**
   * Request manual ACH coordination for RA transfer
   */
  async requestManualPayment(importId, token) {
    const url = `${this.apiBase}${this.endpoints.manualPayment(importId)}`;
    logger.debug('Requesting manual ACH payment for RA transfer', { importId });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify({ payment_method: 'ach' }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to request ACH payment');
      }

      logger.info('Manual ACH payment requested', { importId });
      return result;
    } catch (error) {
      logger.error('Error requesting manual ACH payment', error);
      throw error;
    }
  }

  /**
   * Confirm payment success (alternative to webhook)
   * Call this after Stripe confirms the payment was successful
   */
  async confirmPayment(importId, token) {
    const url = `${this.apiBase}${this.endpoints.confirmPayment(importId)}`;
    logger.debug('Confirming payment for import', { importId });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to confirm payment');
      }

      logger.info('Payment confirmed', { importId });
      return result;
    } catch (error) {
      logger.error('Error confirming payment', error);
      throw error;
    }
  }

  /**
   * Get import by ID
   */
  async getImport(importId, token) {
    const url = `${this.apiBase}${this.endpoints.getImport(importId)}`;
    logger.debug('Fetching import', { importId });

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to get import');
      }

      return result;
    } catch (error) {
      logger.error('Error fetching import', error);
      throw error;
    }
  }

  /**
   * Get import by company ID
   */
  async getImportByCompany(companyId, token) {
    const url = `${this.apiBase}/api/import/by-company/${companyId}`;
    logger.debug('Fetching import by company', { companyId });

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to get import');
      }

      return result;
    } catch (error) {
      logger.error('Error fetching import by company', error);
      throw error;
    }
  }

  /**
   * Get all imports for current user
   */
  async getMyImports(token) {
    const url = `${this.apiBase}${this.endpoints.myImports}`;
    logger.debug('Fetching my imports');

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to get imports');
      }

      return result;
    } catch (error) {
      logger.error('Error fetching imports', error);
      throw error;
    }
  }

  /**
   * Cancel an import
   */
  async cancelImport(importId, token) {
    const url = `${this.apiBase}${this.endpoints.cancel(importId)}`;
    logger.debug('Cancelling import', { importId });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to cancel import');
      }

      logger.info('Import cancelled', { importId });
      return result;
    } catch (error) {
      logger.error('Error cancelling import', error);
      throw error;
    }
  }

  /**
   * Download an import document
   * @param {string} importId - The import ID
   * @param {string} documentId - The document ID
   * @param {string} fileName - The desired file name for download
   * @param {string} token - Auth token
   */
  async downloadDocument(importId, documentId, fileName, token) {
    const url = `${this.apiBase}/api/import/${importId}/documents/${documentId}/download`;
    logger.debug('Getting download URL for import document', {
      importId,
      documentId,
    });

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(token),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to get download URL');
      }

      if (result.success && result.data?.download_url) {
        // Open the download URL in a new window or trigger download
        const downloadUrl = result.data.download_url;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName || result.data.file_name || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        logger.info('Document download initiated', { importId, documentId });
        return result;
      }

      throw new Error('Download URL not available');
    } catch (error) {
      logger.error('Error downloading document', error);
      throw error;
    }
  }
}

// Export singleton instance
const importService = new ImportService();
export default importService;
